import { z } from 'zod';
import { digestSchema } from '../hashing/hash.js';
import { fileIdSchema } from '../files/stored-file.js';
import { stepStateSchema } from './contract.js';
/**
 * Versioned step definitions (ADR-003 rule 5).
 *
 * A definition is split into three tiers because one hash cannot do two jobs:
 *
 *  - `content`  — what the person is attested to have seen and agreed to. Hashed.
 *                 Immutable once any instance has completed against it.
 *  - `presentation` — person-visible wording that carries no evidentiary weight
 *                 (a clearer heading, a typo fix). Editable in place.
 *  - `ops`      — never person-facing (internal notes, routing hints).
 *
 * Only `content` is hashed. The evidentiary question is "did this person read and agree
 * to exactly these words?", so the hashed surface must stay narrow and stable for years:
 * a schema gaining an `estimatedMinutes` field must not invalidate a signature.
 */
export const stepDefinitionContentSchema = z.object({
    /** Validated against the step type's own configSchema — see validateStepConfig. */
    config: z.unknown(),
    /** Present only for types that attest to controlled text. */
    controlledText: z.string().nullable().default(null),
    controlledTextHash: digestSchema.nullable().default(null),
});
export const stepDefinitionPresentationSchema = z.object({
    heading: z.string().min(1),
    helpText: z.string().default(''),
});
export const stepDefinitionOpsSchema = z.object({
    internalNotes: z.string().default(''),
    reviewerHint: z.string().default(''),
});
export const stepDefinitionVersionSchema = z.object({
    definitionId: z.string().min(1),
    version: z.number().int().positive(),
    type: z.string().min(1),
    content: stepDefinitionContentSchema,
    presentation: stepDefinitionPresentationSchema,
    ops: stepDefinitionOpsSchema,
    contentHash: digestSchema,
    /** The exact bytes that were hashed, kept so the digest stays verifiable. */
    canonicalContent: z.string().min(1),
    createdAt: z.string().datetime(),
    createdBy: z.string().min(1),
});
/** The mutable head. Holds a pointer, never content — so it can never drift from a version. */
/**
 * Whether a definition belongs to the catalog or to one person.
 *
 * `person` scope is how an ad-hoc document request (PLAN §7.3) works without becoming a
 * second configuration system: HR asking one candidate for one extra document creates a
 * definition scoped to that person, resolved by exactly the same machinery, never seeded
 * into the catalog and never appearing on anybody else's checklist.
 */
export const definitionScopeSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('catalog') }),
    z.object({ kind: z.literal('person'), personId: z.string().uuid() }),
]);
export const stepDefinitionSchema = z.object({
    id: z.string().min(1),
    type: z.string().min(1),
    currentVersion: z.number().int().positive(),
    retiredAt: z.string().datetime().nullable().default(null),
    scope: definitionScopeSchema.default({ kind: 'catalog' }),
    /** Staff may issue a published controlled form independently of the regular packet. */
    controlledForm: z.boolean().optional(),
    /** Absent retains packet issuance; false requires an explicit staff selection. */
    autoIssue: z.boolean().optional(),
    /** Absent or empty retains the existing all-role applicability. */
    eligibleJobProfileKeys: z.array(z.string().min(1)).optional(),
    /**
     * Which requirements this definition closes (ADR-006 §7).
     *
     * On the HEAD, never inside `stepDefinitionContentSchema`. Retargeting a step at a
     * different requirement is an operational change, not a change to what the person read,
     * so it must not move the content hash and invalidate every existing attestation.
     *
     * The schema existed since #18 but was never composed onto anything, so nothing could
     * actually record the edge it describes.
     */
    satisfies: z.array(z.string().min(1)).default([]),
    contribution: z.enum(['completes', 'contributes']).default('completes'),
});
export function definitionAppliesToPerson(definition, person) {
    if (definition.scope.kind === 'person' && definition.scope.personId !== person.id)
        return false;
    const eligible = definition.eligibleJobProfileKeys ?? [];
    return eligible.length === 0 || eligible.some((key) => person.jobProfileKeys.includes(key));
}
export const controlledFormChoiceSchema = z.object({
    definitionId: z.string().min(1), label: z.string().min(1), type: z.string().min(1), version: z.number().int().positive(),
    eligibleJobProfileKeys: z.array(z.string()), satisfies: z.array(z.string()),
    existingInstanceId: z.string().nullable(),
});
export const controlledFormChoicesSchema = z.object({ forms: z.array(controlledFormChoiceSchema) });
export const issueControlledFormSchema = z.object({ definitionId: z.string().min(1), version: z.number().int().positive() }).strict();
/**
 * What an instance pins.
 *
 * The sealed bytes are embedded, not merely pointed at. A pointer cannot satisfy
 * "render exactly what the person saw", because cosmetic edits are allowed to change the
 * version document in place — in precisely the layer the person could see. Embedding
 * also means the record survives the definition being retired or the version document
 * being lost.
 */
export const pinnedDefinitionSchema = z.object({
    definitionId: z.string().min(1),
    version: z.number().int().positive(),
    contentHash: digestSchema,
    canonicalContent: z.string().min(1),
    presentationSnapshot: stepDefinitionPresentationSchema,
    pinnedAt: z.string().datetime(),
});
/**
 * A partially-completed step, saved as the person works (written by #55).
 *
 * Separate from `submission`, which is what they finally sent and is immutable. A draft is
 * overwritten freely; a submission never is.
 */
export const stepDraftSchema = z.object({
    value: z.unknown(),
    savedAt: z.string().datetime(),
    /** Increments per save, so a late arriving autosave cannot overwrite a newer one. */
    revision: z.number().int().nonnegative(),
});
export const stepInstanceSchema = z.object({
    id: z.string().min(1),
    personId: z.string().min(1),
    /** The instances container also holds upload and packet-snapshot documents. */
    docType: z.literal('step-instance').default('step-instance'),
    type: z.string().min(1),
    state: stepStateSchema,
    pinned: pinnedDefinitionSchema,
    /**
     * `.default(null)` is load-bearing, not decoration. A bare `z.unknown()` infers an
     * OPTIONAL property, because unknown includes undefined — which made StepInstance
     * silently un-assignable to StepInstanceRecord, the shape `approveInstance` takes. The
     * default guarantees the key is present, and the assignability test below the schema
     * proves it stays that way.
     */
    config: z.unknown().default(null),
    submission: z.unknown().default(null),
    attempt: z.number().int().nonnegative().default(0),
    /** Set for a one-off request that has no catalog definition (PLAN §7.3). */
    adHoc: z.boolean().default(false),
    /**
     * Snapshotted from the definition head AT ISSUANCE, deliberately not read live.
     *
     * Retargeting a definition later is a legal in-place edit (ADR-006 §7). If this were
     * read live, that edit would silently rewrite which requirement an already-completed
     * step had closed — changing history rather than the future.
     */
    satisfies: z.array(z.string().min(1)).default([]),
    contribution: z.enum(['completes', 'contributes']).default('completes'),
    issuedAt: z.string().datetime(),
    issuedBy: z.string().min(1),
    sentAt: z.string().datetime().nullable().default(null),
    sentBy: z.string().min(1).nullable().default(null),
    startedAt: z.string().datetime().nullable().default(null),
    submittedAt: z.string().datetime().nullable().default(null),
    reviewedAt: z.string().datetime().nullable().default(null),
    reviewedBy: z.string().min(1).nullable().default(null),
    /**
     * The reason CODE (packages/shared/src/steps/review.ts), not a sentence. What the person
     * reads is derived from it by `presentRejection`, so the words can be improved without
     * rewriting stored records — and so the same rejection reads identically everywhere.
     */
    rejectionReason: z.string().nullable().default(null),
    /**
     * HR's own words about the rejection, for HR. Deliberately separate from the code above
     * and never shown to the candidate: it is where somebody eventually writes a sentence
     * containing a person's document, which must not become the text a candidate is emailed.
     */
    reviewNote: z.string().default(''),
    /**
     * What HR chose to tell the CANDIDATE when sending this step back, beyond the reason sentence
     * (`CANDIDATE_COMMENT_MAX`). Cleared wherever `rejectionReason` is cleared, and only ever shown
     * while the step is `rejected`. Never in an audit payload.
     */
    candidateComment: z.string().max(500).default(''),
    /** Staged uploads belonging to this step, before anything is routed. */
    uploadIds: z.array(z.string().min(1)).default([]),
    /**
     * LEGACY — where a file routed to SharePoint landed (#61). Nothing writes it since ADR-009 and
     * nothing counts it as evidence; it stays readable on retained instances, never rewritten.
     */
    sharePointItemUrl: z.string().nullable().default(null),
    /**
     * The committed FileRecord of this step's approved file (#183, ADR-009 §1). Set by the
     * `store-file` handler only AFTER the bytes are in protected storage and the record appended,
     * so a non-null value means committed. Evidence that needs the document reads this.
     */
    storedFileId: fileIdSchema.nullable().default(null),
    draft: stepDraftSchema.nullable().default(null),
});
/**
 * A stored instance must remain usable by the step engine.
 *
 * `approveInstance` takes a `StepInstanceRecord`, so if this schema ever stops producing
 * that shape, approval breaks — and it breaks at approval time, on a real candidate's
 * submission, not here. This caught exactly that once already: `z.unknown()` infers an
 * OPTIONAL property, so `config` and `submission` were absent from the required shape and
 * nothing noticed, because nothing calls approveInstance until #60.
 */
const _instanceIsARecord = true;
void _instanceIsARecord;
/**
 * Classify an edit by comparing content hashes.
 *
 * PLAN §6 phrases this as "saving an edit asks one question", which would put a
 * compliance-critical judgement in the hands of whoever is clicking. The engine derives
 * it instead and the admin merely confirms: a change to attested content is material by
 * definition, whatever anyone intended.
 */
export function classifyEdit(previousContentHash, nextContentHash) {
    return previousContentHash === nextContentHash ? 'cosmetic' : 'material';
}
/**
 * A cosmetic edit applies in place; a material edit must open a new version.
 * Returns the reason when the intent cannot be honoured, so the caller can refuse the
 * save rather than silently doing something else.
 */
export function checkEditIntent(intent, actual) {
    if (intent === actual)
        return { ok: true };
    if (actual === 'material') {
        return {
            ok: false,
            reason: 'This edit changes attested content, so it cannot be applied in place. Save it as a new version.',
        };
    }
    return {
        ok: false,
        reason: 'Nothing in the attested content changed, so this does not need a new version.',
    };
}
//# sourceMappingURL=definitions.js.map